import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type { PairingResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The pairing inbox's RESPOND mutation (SLICE 1.4-d) — POST
 * /tournaments/pairings/{id}/respond via tournamentApi.respondPairing, body
 * { accept } forwarded VERBATIM. Thin transport: the hook owns no business logic
 * and — crucially — NO money. Accept is CONSENT-ONLY: both players already paid
 * their own per-player share at solo entry, so a respond carries exactly
 * { accept: true | false } and never a payment_method_id / amount (the #1418
 * double-charge guard).
 *
 * On SUCCESS (a fold happened — an Accept turns two solo `partner_wanted` entries
 * into one registered pair) it invalidates:
 *   - pairingsMine(drawId) — the inbox refetches (the responded pairing changes).
 *   - registrationsMine(tournamentId) — the player's row flips to "Registered ·
 *     with X", so the per-event list re-reads the server's truth.
 *   - eligibility(tournamentId) — the fold can change remaining verdicts (EBC
 *     caps), so the verdicts refetch.
 *
 * Every settled command also reconciles personal requests and history, including
 * lost-response/conflict errors. A receipt never replaces the source's status.
 * Tournament-scoped success invalidations remain separate.
 *
 * `tournamentId` keys the registrations + eligibility invalidations (the respond
 * endpoint itself is pairing-scoped); `drawId` keys the inbox.
 */
export function useRespondPairing(drawId: string, tournamentId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    PairingResponse,
    unknown,
    { pairingId: string; accept: boolean }
  >({
    mutationFn: ({ pairingId, accept }) =>
      tournamentApi.respondPairing(pairingId, { accept }),
    onSuccess: async () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.pairingsMine(drawId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.registrationsMine(tournamentId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.eligibility(tournamentId),
      });
      // Accepting a pairing folds two solo entries (the partner's flips to
      // withdrawn), which changes the per-event entry_count on the events-list
      // query — refetch it too so the row's "N entries" count doesn't go stale
      // until a hard refresh.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.events(tournamentId),
      });
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.tournaments.myRegistrations(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.tournaments.myPairingRequests(),
        }),
      ]);
    },
  });
}
